import { useState } from 'react';
import { Checkbox, MenuItem } from '@mui/material';
import { useTextFieldStyles } from '../text-field/text-field.styles';
import clsx from 'clsx';
import { useTextStyles } from '../../../theme/text';
import { useDropdownStyles } from './dropdown.styles';
import { TextField, TextFieldProps, TextFieldVariant } from '../text-field/text-field.component';
import { decodeImageURL } from '../../../util/image-url';

export interface MultiSelectDropdownProps<T>
  extends Omit<TextFieldProps, 'onChange' | 'textFieldVariant' | 'condensed' | 'startAdornment'> {
  dropdownVariant: TextFieldVariant;
  shrinkLabel?: boolean;
  textFieldDesktopWidth?: string;
  menuItems: { label: string; imageURL?: string; value: T }[];
  textColorOverride?: string;
  width?: string;
  multiple?: boolean; // Add the multiple prop
  onChange: (value: T[]) => void;
}

export function MultiSelectDropdown<T extends string | number>({
  dropdownVariant = 'floating',
  textFieldDesktopWidth,
  placeholder,
  menuItems,
  className,
  textColorOverride,
  width,
  multiple,
  onChange,
  slotProps,
  ...props
}: MultiSelectDropdownProps<T>) {
  const classes = useDropdownStyles({ variant: dropdownVariant });
  const textFieldClasses = useTextFieldStyles({ variant: dropdownVariant });
  const textClasses = useTextStyles();

  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<T[]>(
    props.value
      ? typeof props.value === 'string'
        ? (props.value.split(',') as T[])
        : Array.isArray(props.value)
        ? (props.value as T[])
        : []
      : []
  );

  // MUI Select's onChange event is a union; the value is whatever we put on the MenuItems.
  const handleSelectChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void = (event) => {
    const value = (event.target as { value: unknown }).value;
    const newSelectedValues = (Array.isArray(value) ? value : [value]) as T[];
    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  return (
    <TextField
      select
      focused={open}
      placeholder={placeholder}
      textFieldVariant={dropdownVariant}
      desktopWidth={textFieldDesktopWidth}
      textColorOverride={textColorOverride}
      {...props}
      // A slot the caller passes replaces ours outright.
      slotProps={{
        ...slotProps,
        input: slotProps?.input ?? {
          className: clsx(classes.dropdownIcon, textFieldClasses.textField, classes.select)
        },
        select:
          slotProps?.select ??
          ({
            multiple: multiple,
            value: selectedValues,
            onChange: handleSelectChange,
            renderValue: (selected) => {
              if (Array.isArray(selected)) {
                return selected.map((value) => menuItems.find((item) => item.value === value)?.label).join(', ');
              }
              return menuItems.find((item) => item.value === selected)?.label || '';
            },
            MenuProps: {
              style: textColorOverride ? { color: textColorOverride } : {},
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            },
            open,
            onOpen: () => setOpen(true),
            onClose: () => setOpen(false),
            style: textColorOverride ? { color: textColorOverride } : {}
          } as any)
      }}
    >
      {menuItems.map(({ label, imageURL, value }) => (
        <MenuItem className={textClasses.dropdownMenu} key={value} value={value}>
          {multiple ? <Checkbox checked={selectedValues.includes(value)} /> : null}
          {imageURL ? <img src={decodeImageURL(imageURL)} alt={label} style={{ height: 30 }} /> : label}
        </MenuItem>
      ))}
    </TextField>
  );
}
